import React, { useMemo } from 'react';
import { Dropdown, Button, StatusTag, Typography } from '../../../../../components';
import { StorageCriteria, MemoryCriteria } from './components/StorageAndMemoryCriteria';
import { INVENTORY_STATUS } from '../../../../../constants/discovery';
import { MoreOutlined, UpCircleOutlined, DownCircleOutlined } from '@ant-design/icons';
import { ipToNumber, parseCpuData } from '../../../../../lib/helpers';
import { returnPowerState } from '../../../helpers';
import { getCriteriaIcon } from './components/StorageAndMemoryCriteria/CriteriaIcons';
import { returnTotalDiskCriteria } from '../../../helpers';

const { Text } = Typography;

export const createTableColumns = ({ handleChangeReadyStatus = () => {} }) => {
    const columns = useMemo(
        () => [
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'ready_status',
                width: 120,
                align: 'center',
                onHeaderCell: () => ({
                    className: 'column-header status-header',
                }),
                render: (text, row) => {
                    return (
                        <StatusTag
                            text={INVENTORY_STATUS[row.migration_ready]?.text}
                            toolTip={INVENTORY_STATUS[row.migration_ready]?.toolTip}
                        />
                    );
                },
            },
            // {
            //     title: 'Type',
            //     dataIndex: 'type',
            //     key: 'type',
            //     width: 84,
            //     align: 'center',
            //     onHeaderCell: () => ({
            //         className: 'column-header'
            //     }),
            // },
            {
                title: 'VM Name',
                dataIndex: 'name',
                key: 'name',
                sortKey: 'name',
                width: 250,
                align: 'left',
                sorter: true,
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    return <Text>{text || 'NA'}</Text>;
                },
            },
            {
                title: 'IP',
                dataIndex: 'ip_addresses',
                key: 'ip',
                width: 227,
                align: 'left',
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    return <Text>{text[0] || 'NA'}</Text>;
                },
            },
            {
                title: 'Hostname',
                dataIndex: 'host_name',
                sortKey: 'hostName',
                key: 'host',
                width: 250,
                align: 'left',
                sorter: true,
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    return <Text>{row.host_name || 'NA'}</Text>;
                },
            },
            {
                title: 'Power State',
                dataIndex: 'power_state',
                sortKey: 'powerState',
                key: 'powerState',
                width: 227,
                align: 'left',
                sorter: true,
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    return (
                        <div className="power-state">
                            <span className={`power-state-icon power-state-icon--${row.power_state}`}></span>
                            <Text>{returnPowerState(row)}</Text>
                        </div>
                    );
                },
            },
            {
                title: 'Reported OS Version',
                dataIndex: 'reported_os_version',
                key: 'reportedOsVersion',
                sortKey: 'reportedOSVersion',
                width: 350,
                align: 'left',
                sorter: true,
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    let osVersion = row.reported_os_version || '';
                    if (!osVersion) {
                        return;
                    }
                    return (
                        <Text className="icon-center">
                            {osVersion} {row.can_modernize_os && getCriteriaIcon('can_modernize_os')}
                        </Text>
                    );
                },
            },
            {
                title: 'Configured OS Version',
                dataIndex: 'configured_os_version',
                key: 'configuredOsVersion',
                sortKey: 'configuredOSVersion',
                width: 350,
                align: 'left',
                sorter: true,
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    let osVersion = row.configured_os_version || '';
                    if (!osVersion) {
                        return;
                    }
                    return (
                        <Text className="icon-center">
                            {osVersion} {row.can_modernize_os && getCriteriaIcon('can_modernize_os')}
                        </Text>
                    );
                },
            },

            // Columns that are not visible by default
            {
                title: 'Tags',
                dataIndex: 'tags',
                key: 'tags',
                width: 150,
                align: 'left',
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
            },
            {
                title: 'Move Group',
                dataIndex: 'move_group_name',
                key: 'moveGroup',
                width: 200,
                align: 'left',
                sorter: (a, b) => (a.move_group_name || '').localeCompare(b.move_group_name || ''),
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    return <Text>{row.move_group_name || 'NA'}</Text>;
                },
            },
            {
                title: 'Application',
                dataIndex: 'application_name',
                key: 'application',
                width: 200,
                align: 'left',
                sorter: (a, b) => (a.application_name || '').localeCompare(b.application_name || ''),
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    return <Text>{row.application_name || 'NA'}</Text>;
                },
            },
            {
                title: 'vCPUs',
                dataIndex: 'architecture',
                key: 'vcpus',
                width: 150,
                align: 'left',
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    const cpuData = parseCpuData(row?.cpus);
                    const vCpus = cpuData?.cores && cpuData?.sockets ? cpuData.cores * cpuData.sockets : null;
                    return (
                        <Text className="icon-center">
                            {vCpus ?? 'N/A'} {getCriteriaIcon(row.cpu_utilization)}
                        </Text>
                    );
                },
            },
            // Not using this column in V1 of this feature
            // {
            //     title: 'Platform',
            //     dataIndex: 'platform',
            //     key: 'platform',
            //     width: 150,
            //     align: 'center',
            //     onHeaderCell: () => ({
            //         className: 'column-header'
            //     }),
            // },
            {
                title: 'Storage',
                dataIndex: 'storage',
                key: 'storage',
                width: 340,
                align: 'left',
                onHeaderCell: () => ({
                    className: 'column-header',
                }),
                render: (text, row) => {
                    // Calculate total and used storage across all disks
                    const { criteria, totalSpace, totalUsed } = returnTotalDiskCriteria(row);

                    return (
                        <StorageCriteria
                            criteria={criteria}
                            total={totalSpace}
                            used={totalUsed}
                            disks={row.storage_disks}
                            disksUsed={row.storage_disks_used}
                            storageUtilization={row.storage_utilization}
                        />
                    );
                },
            },
            {
                title: 'Memory',
                dataIndex: 'memory',
                key: 'memory',
                width: 340,
                align: 'left',
                onHeaderCell: () => ({
                    className: 'column-header align-top',
                }),
                onCell: () => ({
                    className: 'align-top',
                }),
                render: (text, row) => {
                    return (
                        <MemoryCriteria
                            criteria={row.ram_utilization}
                            total={row.ram_total}
                            used={row.ram_used}
                            name={row.name}
                        />
                    );
                },
            },

            // Row actions, visible on row hover
            {
                title: '',
                key: 'actions',
                width: 40,
                align: 'center',
                fixed: 'right',
                onHeaderCell: () => ({
                    className: 'column-header actions-header',
                }),
                onCell: (record) => {
                    return { className: 'actions-cell' };
                },
                render: (_, record) => {
                    return (
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: '1',
                                        label: 'Mark Ready',
                                        onClick: (e) => {
                                            e.domEvent.stopPropagation();
                                            handleChangeReadyStatus([record.id], 'ready', [record]);
                                        },
                                    },
                                    {
                                        key: '2',
                                        label: 'Mark Not Ready',
                                        onClick: (e) => {
                                            e.domEvent.stopPropagation();
                                            handleChangeReadyStatus([record.id], 'not_ready', [record]);
                                        },
                                    },
                                ],
                            }}
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                icon={<MoreOutlined className="inventory-row-action-icon" />}
                                onClick={(e) => e.stopPropagation()}
                                className="row-action-button"
                            />
                        </Dropdown>
                    );
                },
            },
        ],
        [],
    );

    return columns;
};
